// frontend/src/components/sections/HeroSection.tsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useSiteSettingsStore } from '@/store/settingsStore';
import { Loader2, BarChartBig, PackageSearch, ChevronRight } from 'lucide-react';
import { type HomepageHeroImage } from '@/types';

// Fallback default images if nothing is configured in settings OR settings are loading
import defaultBanner1_newYork from '../../assets/hero-banners/home-banner-new-york.jpg';
import defaultBanner2_chicago from '../../assets/hero-banners/home-banner-chicago.jpg';
// Add other default images if you want a non-empty slideshow when settings are not configured
// import singaporeBanner from '../../assets/hero-banners/home-banner-singapore.jpg';

const defaultStaticHeroImages: HomepageHeroImage[] = [
  { url: defaultBanner1_newYork, alt_text: 'New York skyline - vibrant financial hub' },
  { url: defaultBanner2_chicago, alt_text: 'Chicago architecture by the river' },
  // { url: singaporeBanner, alt_text: 'Singapore skyline at night' },
];

const SLIDE_INTERVAL_MS = 7000; 

const HeroSection = () => {
  const navigate = useNavigate();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [visibleSlot, setVisibleSlot] = useState(0); 
  const slideIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const publicSettings = useSiteSettingsStore(state => state.publicSettings);
  const isLoadingSettings = useSiteSettingsStore(state => state.isLoading);

  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  // --- Dynamic Content from Settings ---
  const heroTitle = isLoadingSettings 
    ? "Loading Title..." 
    : publicSettings?.home_hero_main_title || "Navigate the Future of Finance with Vedas Trading"; // Default title
  
  const heroSubtitle = isLoadingSettings 
    ? "Loading tagline..." 
    : publicSettings?.home_hero_subtitle || (publicSettings?.site_tagline || "Empowering your financial journey...");

  // Use configured hero images from settings; fallback to static default images if none configured or still loading
  const configuredHeroImages = publicSettings?.home_hero_images_json;
  const imagesForSlideshow = (configuredHeroImages && configuredHeroImages.length > 0) 
    ? configuredHeroImages 
    : defaultStaticHeroImages;

  // Page visibility for buttons
  const articlesPageEnabled = isLoadingSettings ? true : publicSettings?.page_articles_enabled ?? true;
  const productsPageEnabled = isLoadingSettings ? true : publicSettings?.page_products_enabled ?? true;
  
  const getFullImageUrl = (relativePath: string | null | undefined): string | undefined => {
    if (!relativePath) return undefined;

    // Check if it's one of the directly imported default images
    const defaultImageMatch = defaultStaticHeroImages.find(img => img.url === relativePath);
    if (defaultImageMatch) {
        return defaultImageMatch.url; // Return the direct import
    }

    if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
      return relativePath; // Absolute URL
    }
    
    const cleanBase = backendBaseUrl.endsWith('/') ? backendBaseUrl.slice(0, -1) : backendBaseUrl;
    
    // Handle paths like "/static/uploads/hero/image.jpg" or "static/uploads/hero/image.jpg"
    if (relativePath.startsWith('/static/')) {
        return `${cleanBase}${relativePath}`;
    }
    if (relativePath.startsWith('static/')) {
        return `${cleanBase}/${relativePath}`;
    }
    // Assume it's a path like "uploads/hero_banners/my-image.jpg" and needs "/static/" prepended
    return `${cleanBase}/static/${relativePath.startsWith('/') ? relativePath.substring(1) : relativePath}`;
  };


  useEffect(() => {
    if (imagesForSlideshow.length > 0) {
        imagesForSlideshow.forEach((imgData: any) => {
            if (imgData && imgData.url) { // Check if imgData and imgData.url are defined
                const img = new Image();
                const fullUrl = getFullImageUrl(imgData.url);
                if (fullUrl) img.src = fullUrl; // Preload
            }
        });
    }

    const advanceSlide = () => {
      if (imagesForSlideshow.length > 0) {
        setActiveImageIndex(prevIndex => (prevIndex + 1) % imagesForSlideshow.length);
        setVisibleSlot(prevSlot => 1 - prevSlot); 
      }
    };

    if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
    }
    if (imagesForSlideshow.length > 1) { 
        slideIntervalRef.current = setInterval(advanceSlide, SLIDE_INTERVAL_MS);
    } else {
        setActiveImageIndex(0);
        setVisibleSlot(0);
    }

    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
    };
  }, [imagesForSlideshow]); // Re-run effect if imagesForSlideshow array changes


  let imageForSlot0: string | undefined = undefined;
  let imageForSlot1: string | undefined = undefined;
  let altForSlot0: string | undefined = heroTitle; // Default alt to hero title
  let altForSlot1: string | undefined = heroTitle;

  if (imagesForSlideshow.length > 0) {
    const currentActiveIndex = activeImageIndex % imagesForSlideshow.length;
    const prevActiveIndex = (activeImageIndex - 1 + imagesForSlideshow.length) % imagesForSlideshow.length;

    const idx0 = visibleSlot === 0 ? currentActiveIndex : prevActiveIndex;
    const idx1 = visibleSlot === 1 ? currentActiveIndex : prevActiveIndex;
    
    imageForSlot0 = getFullImageUrl(imagesForSlideshow[idx0]?.url);
    altForSlot0 = imagesForSlideshow[idx0]?.alt_text || heroTitle; 
    
    const safeIdx1 = imagesForSlideshow.length > 1 ? idx1 : idx0;
    imageForSlot1 = getFullImageUrl(imagesForSlideshow[safeIdx1]?.url);
    altForSlot1 = imagesForSlideshow[safeIdx1]?.alt_text || heroTitle;

  } else { // Fallback if imagesForSlideshow is unexpectedly empty
    imageForSlot0 = "https://placehold.co/1920x1080/1a0e0d/FFFFFF?text=Vedas+Trading+Banner";
    imageForSlot1 = "https://placehold.co/1920x1080/1a0e0d/FFFFFF?text=Financial+Insights";
    altForSlot0 = "Vedas Trading Placeholder Banner";
    altForSlot1 = "Financial Insights Placeholder Banner";
  }

  return (
    <section
      className="relative flex flex-col items-center justify-center text-center min-h-[70vh] md:min-h-[80vh] p-4 sm:p-8 bg-background text-foreground overflow-hidden"
    >
      <div
        className={`hero-image-slot ${visibleSlot === 0 ? 'active' : ''}`}
        style={{ backgroundImage: imageForSlot0 ? `url(${imageForSlot0})` : 'none', backgroundColor: !imageForSlot0 ? '#1a0e0d' : undefined }}
        role="img"
        aria-label={altForSlot0}
      />
      <div
        className={`hero-image-slot ${visibleSlot === 1 ? 'active' : ''}`}
        style={{ backgroundImage: imageForSlot1 ? `url(${imageForSlot1})` : 'none', backgroundColor: !imageForSlot1 ? '#1a0e0d' : undefined }}
        role="img"
        aria-label={altForSlot1}
      />

      <div className="absolute inset-0 z-[5] bg-gradient-to-br from-primary/60 via-accent/40 to-vedasMaroon-700/50 opacity-70 dark:opacity-60"></div>
      <div className="absolute inset-0 z-[6] bg-gradient-to-t from-background/70 to-transparent opacity-50"></div>

      <div className="relative z-10 flex w-full items-center justify-center">
        <div className="bg-background/85 dark:bg-black/70 backdrop-blur-lg p-6 sm:p-8 md:p-10 rounded-xl shadow-2xl max-w-xs sm:max-w-md md:max-w-2xl lg:max-w-3xl xl:max-w-4xl text-center">
          <div className="space-y-6">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-tight text-gradient-maroon-to-gold">
              {heroTitle}
            </h1>
            <p className="max-w-2xl mx-auto text-lg md:text-xl text-white dark:text-foreground/85">
              {heroSubtitle}
            </p>
            
            {isLoadingSettings && (!publicSettings?.page_articles_enabled && !publicSettings?.page_products_enabled) && ( // Show loader only if buttons would be hidden by loading state
              <div className="flex justify-center pt-4">
                <Loader2 className="h-8 w-8 animate-spin text-gold-light" />
              </div>
            )}

            {!isLoadingSettings && (articlesPageEnabled || productsPageEnabled) && (
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-4">
                {articlesPageEnabled && (
                  <Button
                    size="lg"
                    onClick={() => navigate('/articles')}
                    className="!bg-gradient-to-r !from-primary !to-vedasMaroon-700 hover:!from-primary/90 hover:!to-vedasMaroon-700/90 text-primary-foreground text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300 !cursor-pointer w-full sm:w-auto group"
                  >
                     <BarChartBig className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:rotate-[15deg]" />
                    Explore Insights
                     <ChevronRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                )}
                {productsPageEnabled && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate('/products')}
                    className="border-primary text-primary hover:bg-primary/5 dark:border-accent dark:text-accent dark:hover:bg-accent/10 text-lg px-8 py-6 shadow-sm hover:shadow-md transition-all duration-300 group w-full sm:w-auto"
                  >
                    <PackageSearch className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
                    <span className="group-hover:text-gradient-maroon-to-gold transition-colors duration-300 !cursor-pointer">Our Products</span>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
